import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TIME_FORMAT } from '@/app/constants/date'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { DateField } from '@/ui/form/controls/DateField'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { toast } from '@/lib/toasts/api'
import { useGlobalDateRange } from './useGlobalDateRange'

interface BusinessTripEditTachographViewProps {
  tripData: any
  companyId: string
  driverId: string
  onCancel: () => void
  onSave: (events: EventModel[]) => void
}

const TachographColumns: TableHeaderColumn[] = [
  { title: 'DATA', key: 'date', type: 'string', sort: false },
  { title: 'KRAJ', key: 'country', type: 'string', sort: false },
  { title: 'PRZYJAZD', key: 'isArrival', type: 'string', sort: false },
  { title: 'TRANSIT', key: 'isTransit', type: 'string', sort: false },
]

const BusinessTripEditTachographView: React.FC<BusinessTripEditTachographViewProps> = ({
  tripData,
  companyId,
  driverId,
  onCancel,
  onSave,
}) => {
  const { t } = useLocalize()
  const { getGpsFromTachograph } = useBusinessTripService()

  const [tachographData, setTachographData] = React.useState<GpsFromTachographModel[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  const globalDateRange = useGlobalDateRange()

  const [dateFrom, setDateFrom] = React.useState<string>(globalDateRange.from)
  const [dateTo, setDateTo] = React.useState<string>(globalDateRange.to)
  const [showCountryArrival, setShowCountryArrival] = React.useState(true)
  const [showCountryLeave, setShowCountryLeave] = React.useState(true)
  const [checkUncheckTransitRows, setCheckUncheckTransitRows] = React.useState(false)

  const filteredData = tachographData.filter((item) => {
    if (!showCountryArrival && item.isArrival) return false
    if (!showCountryLeave && !item.isArrival) return false
    return true
  })

  const allChecked = filteredData.length > 0 && filteredData.every((item) => item.checked)

  React.useEffect(() => {
    const fetchTachographData = async () => {
      if (!driverId || !dateFrom || !dateTo) {
        setTachographData([])
        return
      }

      try {
        setIsLoading(true)
        const data = await getGpsFromTachograph({
          companyId,
          driverId,
          startTime: dateFrom,
          endTime: dateTo,
        })
        // Data is already mapped by service, just ensure checked property exists
        setTachographData(
          (data || []).map((item: GpsFromTachographModel) => ({
            ...item,
            checked: item.checked || false,
          }))
        )
      } catch (err: any) {
        console.error('Error fetching tachograph data:', err)
        // Only show error if it's an actual API error (has response), not a validation issue
        if (err?.response) {
          toast.error({
            text: err?.response?.data?.message || err?.message || t('errorCodes.retrieving-gps-from-tachograph-failed'),
          })
        }
        setTachographData([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchTachographData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driverId, dateFrom, dateTo])

  React.useEffect(() => {
    if (checkUncheckTransitRows) {
      setTachographData((prev) =>
        prev.map((item) => ({
          ...item,
          checked: item.isTransit ? false : item.checked,
        }))
      )
      setCheckUncheckTransitRows(false)
    }
  }, [checkUncheckTransitRows])

  const handleToggleAll = (checked: boolean) => {
    setTachographData((prev) =>
      prev.map((item) => {
        if (!showCountryArrival && item.isArrival) return item
        if (!showCountryLeave && !item.isArrival) return item
        return { ...item, checked }
      })
    )
  }

  const handleToggleItem = (id: string | undefined, checked: boolean) => {
    if (!id) return
    setTachographData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked } : item))
    )
  }

  const addItemBasedOnGpsData = (
    date: Moment | null,
    country: any,
    city: string | null,
    tripEvent: TripEventType,
    transit: boolean,
    existingItems: Moment[],
    lp: number
  ): EventModel | null => {
    if (!date) return null

    if (existingItems.length > 0) {
      for (const item of existingItems) {
        if (item.format(TIME_FORMAT) === date.format(TIME_FORMAT)) {
          return null
        }
      }
    }

    return {
      date,
      isTransit: transit,
      tripEvent,
      country: country || null,
      city: city || undefined,
      lp,
    }
  }

  const handleSave = () => {
    const selectedItems = filteredData.filter((item) => item.checked)
    if (selectedItems.length === 0) {
      toast.error({
        text: t('newDelegation.no-items-selected'),
      })
      return
    }

    const existingItems: Moment[] = (tripData.events || []).map((e: any) =>
      moment(e.date)
    )
    const events: EventModel[] = []

    for (let i = 0; i < selectedItems.length; i++) {
      const tachographCurrent = selectedItems[i]
      if (!tachographCurrent.country || !tachographCurrent.date) continue

      const event = addItemBasedOnGpsData(
        moment(tachographCurrent.date),
        tachographCurrent.country,
        null,
        TripEventType.ChangeCountry,
        tachographCurrent.isTransit || false,
        existingItems,
        i
      )
      if (event) {
        events.push(event)
        existingItems.push(moment(tachographCurrent.date))
      }
    }

    const filteredEvents = events.filter((e) => e != null)
    if (filteredEvents.length > 0) {
      onSave(filteredEvents)
      toast.success({
        text: t('newDelegation.tachograph-events-created'),
      })
    } else {
      toast.warning({
        text: t('newDelegation.no-events-created'),
      })
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-base-50">
      {/* Header with Save/Cancel */}
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-3">
        <span className="tw-text-sm tw-font-medium tw-text-content-base">
          {t('newDelegation.import-from-tachograph')}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90"
          >
            {t('common.save')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-4">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-4">
          <DateField
            label={t('newDelegation.dateFrom')}
            value={dateFrom}
            setValue={setDateFrom}
            placeholder="YYYY-MM-DD"
          />
          <DateField
            label={t('newDelegation.dateTo')}
            value={dateTo}
            setValue={setDateTo}
            placeholder="YYYY-MM-DD"
          />
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showCountryArrival"
              checked={showCountryArrival}
              onChange={(e) => setShowCountryArrival(e.target.checked)}
            />
            <label
              htmlFor="showCountryArrival"
              className="tw-text-sm tw-text-content-base tw-opacity-70"
            >
              {t('newDelegation.show-country-arrival')}
            </label>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="showCountryLeave"
              checked={showCountryLeave}
              onChange={(e) => setShowCountryLeave(e.target.checked)}
            />
            <label
              htmlFor="showCountryLeave"
              className="tw-text-sm tw-text-content-base tw-opacity-70"
            >
              {t('newDelegation.show-country-leave')}
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-bg-white">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('common.loading')}</span>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('businessTrip.no-data')}</span>
          </div>
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <TableHeader
                columns={TachographColumns}
                toggleAll={handleToggleAll}
                isSelectedAny={allChecked}
                sortConfig={null}
              />
              <tbody>
                {filteredData.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className="tw-border-b tw-border-base-200 hover:tw-bg-base-50"
                  >
                    <td className="tw-px-4 tw-py-2">
                      <Checkbox
                        id={`tachograph_${item.id || index}`}
                        checked={item.checked || false}
                        onChange={(e) => handleToggleItem(item.id, e.target.checked)}
                      />
                    </td>
                    <TableTextRow>
                      {item.date ? moment(item.date).format('DD-MM-YYYY HH:mm') : '-'}
                    </TableTextRow>
                    <TableTextRow>{item.country?.name || '-'}</TableTextRow>
                    <TableTextRow>
                      {item.isArrival ? t('common.yes') : t('common.no')}
                    </TableTextRow>
                    <TableTextRow>
                      {item.isTransit ? t('common.yes') : t('common.no')}
                    </TableTextRow>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  )
}

export default BusinessTripEditTachographView

